import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, Users, BookOpen,
  Briefcase, Target, Star, Calendar, Facebook, Instagram, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RTBLogo } from '@/components/RTBLogo';
import { LegalFooter } from '@/components/LegalFooter';
import { getPublicSettings } from '@/lib/api';

const PROGRAM_PHASES = [
  { icon: Target, title: "Plan", day: "Days 1-5", subtitle: "Foundation Phase", desc: "LLC formation, business identity, mindset training, and your entrepreneurial foundation.", gradient: "from-rtb-purple to-rtb-purple-light" },
  { icon: BookOpen, title: "Prepare", day: "Days 6-10", subtitle: "Structure Phase", desc: "Business strategy, financial literacy, tax optimization training, and credit building.", gradient: "from-rtb-green to-emerald-400" },
  { icon: Briefcase, title: "Prosper", day: "Days 11-15", subtitle: "Activation Phase", desc: "Launch your business, first clients, revenue strategies, and marketing fundamentals.", gradient: "from-rtb-purple-light to-rtb-green" },
  { icon: Star, title: "Scale", day: "Days 16-20", subtitle: "Expansion Phase", desc: "Growth systems, team building, advanced strategies, and your graduation roadmap.", gradient: "from-rtb-green to-rtb-purple" },
];

const INCLUDED = [
  "20-Day Structured Mentorship Program",
  "LLC Formation Assistance (All 50 States)",
  "AI-Powered LLC Filing Fee Assistance",
  "Business Development Questionnaire & Path Matching",
  "Tax Optimization & Financial Literacy Training",
  "Credit Building Strategies (670+ in 45 Days)",
  "Access to Up to $250,000 in Business Funding",
  "Weekly Group Mastery Sessions (Wed & Sat)",
  "Digital Mentorship Contract & Accountability",
  "Business Ebook & Resource Library",
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] } })
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } }
};

export default function LandingPage() {
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getPublicSettings().then(setSettings).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RTBLogo data-testid="nav-logo" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm text-gray-500 hover:text-rtb-purple transition-colors font-medium" data-testid="login-link">
              Sign In
            </Link>
            <Link to="/register">
              <Button className="btn-primary text-xs px-6 py-3" data-testid="nav-enroll-btn">Enroll Now</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero - Dark Section */}
      <section className="pt-28 pb-24 px-6 relative overflow-hidden bg-obsidian text-white" data-testid="hero-section">
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-rtb-purple/15 rounded-full blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[400px] bg-rtb-green/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 px-5 py-2.5 bg-rtb-green/10 border border-rtb-green/20 rounded-full mb-8">
              <Users className="w-4 h-4 text-rtb-green" />
              <span className="text-rtb-green text-sm font-semibold">984+ Families Served</span>
            </motion.div>

            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-7xl font-oswald font-bold uppercase leading-[0.95]">
              We Don't Just Start<br />Businesses — We Build<br />
              <span className="gradient-text">Entrepreneurs</span>
            </motion.h1>

            <motion.p variants={fadeUp} custom={2} className="text-base lg:text-lg text-white/50 max-w-2xl mx-auto mt-8 leading-relaxed">
              A 20-day mentorship program designed to take you from idea to legally formed,
              funded, and operating business. <span className="text-rtb-green font-semibold">Plan. Prepare. Prosper.</span>
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link to="/register">
                <Button className="btn-primary text-lg px-10 py-6 purple-glow" data-testid="hero-enroll-btn">
                  Start Your Journey — $75 <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              {settings.calendly_intro_link && (
                <a href={settings.calendly_intro_link} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-rtb-green/30 text-rtb-green hover:bg-rtb-green/10 text-lg px-8 py-6 rounded-full" data-testid="hero-book-btn">
                    <Calendar className="mr-2 w-5 h-5" /> Book Free Intro Call
                  </Button>
                </a>
              )}
            </motion.div>

            <motion.p variants={fadeUp} custom={4} className="text-white/20 text-sm mt-6">$400 Mastermind upgrade available after enrollment</motion.p>
          </motion.div>
        </div>
      </section>

      {/* Video Section - Light - RTBI Marketing Video */}
      <section className="py-20 px-6 bg-white relative" data-testid="video-section">
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-gray-50" />
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative aspect-video bg-gradient-to-br from-rtb-purple/5 to-rtb-green/5 rounded-2xl overflow-hidden border border-gray-200 shadow-xl shadow-gray-100/50"
            data-testid="rtbi-intro-video"
          >
            <video
              className="w-full h-full object-cover"
              controls
              poster=""
              preload="metadata"
            >
              <source src={`${process.env.REACT_APP_BACKEND_URL}/api/marketing-video`} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>
          <p className="text-center text-gray-400 text-xs mt-4">What is RTBI &amp; What to Expect</p>
        </div>
      </section>

      {/* What's Included - Light with cards */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative" data-testid="included-section">
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-obsidian" />
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-rtb-green text-sm font-bold uppercase tracking-widest">Everything You Get</motion.span>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-5xl font-oswald font-bold uppercase mt-4 text-gray-900">
              Your <span className="text-rtb-purple">$75</span> Investment Includes
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-3">
            {INCLUDED.map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                whileHover={{ scale: 1.02, y: -2 }}
                className="flex items-start gap-3 p-5 bg-white border border-gray-100 hover:border-rtb-purple/20 hover:shadow-lg hover:shadow-rtb-purple/5 transition-all duration-300 cursor-default rounded-xl"
              >
                <CheckCircle2 className="w-5 h-5 text-rtb-green mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 20-Day Program - Dark Section */}
      <section className="py-24 px-6 bg-obsidian text-white relative" data-testid="program-section">
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-gray-50" />
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-rtb-purple-light text-sm font-bold uppercase tracking-widest">The Program</motion.span>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-5xl font-oswald font-bold uppercase mt-4">
              <span className="text-rtb-purple">Plan.</span>{' '}
              <span className="text-rtb-green">Prepare.</span>{' '}
              <span className="text-rtb-purple-light">Prosper.</span>
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROGRAM_PHASES.map((phase, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                whileHover={{ y: -8, scale: 1.02 }}
                className="relative p-6 bg-white/5 border border-white/10 hover:border-rtb-purple/40 transition-all group rounded-2xl overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${phase.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-rtb-purple/10 flex items-center justify-center rounded-xl mb-4 group-hover:bg-rtb-purple/20 transition-colors">
                    <phase.icon className="w-6 h-6 text-rtb-purple" />
                  </div>
                  <div className="text-rtb-green text-xs font-bold uppercase tracking-wider mb-1">{phase.day}</div>
                  <h3 className="font-oswald text-2xl font-bold mb-1">{phase.title}</h3>
                  <p className="text-white/30 text-xs mb-3">{phase.subtitle}</p>
                  <p className="text-white/50 text-sm leading-relaxed">{phase.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* LLC Section - Light */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative" data-testid="llc-section">
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-obsidian" />
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}><Shield className="w-14 h-14 text-rtb-green mx-auto mb-6" /></motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-5xl font-oswald font-bold uppercase mb-6 text-gray-900">
              LLC Formation Made <span className="text-rtb-green">Simple</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-gray-500 max-w-2xl mx-auto mb-12">
              We guide you step-by-step through forming your LLC in any of the 50 states.
              Our AI assistant walks you through every step to get your entity created at zero to low cost.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="grid sm:grid-cols-3 gap-4 mb-12">
              {[
                { val: "50", label: "States Covered" },
                { val: "AI", label: "Assistant" },
                { val: "$0", label: "Target Filing Cost" },
              ].map((s, i) => (
                <motion.div key={i} whileHover={{ y: -4, scale: 1.02 }}
                  className="p-8 bg-white border border-gray-100 hover:border-rtb-green/30 hover:shadow-xl transition-all rounded-2xl">
                  <p className="text-rtb-green font-oswald text-5xl font-bold">{s.val}</p>
                  <p className="text-gray-400 text-sm mt-2">{s.label}</p>
                </motion.div>
              ))}
            </motion.div>
            <motion.div variants={fadeUp} custom={4}>
              <Link to="/register">
                <Button className="btn-green text-lg px-10 py-6 green-glow" data-testid="llc-enroll-btn">
                  Get Started <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA - Dark */}
      <section className="py-24 px-6 bg-obsidian text-white">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-3xl mx-auto text-center">
          <motion.div variants={fadeUp}>
            <RTBLogo dark className="h-16" />
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-5xl font-oswald font-bold uppercase mb-6">
            Ready to Build It — <span className="gradient-text">Your Future?</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-white/50 mb-10">
            Join 984+ families who have taken control of their financial destiny.
          </motion.p>
          <motion.div variants={fadeUp} custom={3}>
            <Link to="/register">
              <Button className="btn-primary text-lg px-12 py-6 purple-glow">
                Enroll Now — $75 <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <RTBLogo className="h-8" />
          <div className="flex items-center gap-6">
            {settings.facebook_page && (
              <a href={`https://facebook.com/${settings.facebook_page.replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-rtb-purple transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
            )}
            {settings.instagram_handle && (
              <a href={`https://instagram.com/${settings.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-rtb-purple transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>
      </footer>
      <LegalFooter />
    </div>
  );
}
